<template>
  <AnalyticsWidget
    title="Contact Order Variations"
    subTitle="List of contacts sorted by changes in order values"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    >
    <div class="row no-gutters h-100">
      <div class="col-12 p-2 h-100">
        <ReportFilterPopover
          headerTitle="Date Range Filter"
          :showApplyButton="true"
          :showResetButton="true"
          :appendToBody="true"
          alignment="left"
          placement="bottom-end"
          @apply="applyContactOrdersFilter"
          @reset="resetContactOrdersFilter">
          <template v-slot:popover>
            <ReportFilterGroup label="Previous Range">
              <ReportDateRangeSelect 
                :startDate="editingContactOrdersFilter.previousStartDate"
                :endDate="editingContactOrdersFilter.previousEndDate"
                @updateStartDate="editingContactOrdersFilter.previousStartDate = $event"
                @updateEndDate="editingContactOrdersFilter.previousEndDate = $event"
              />
            </ReportFilterGroup>
            <ReportFilterGroup label="Current Range">
              <ReportDateRangeSelect 
                :startDate="editingContactOrdersFilter.currentStartDate"
                :endDate="editingContactOrdersFilter.currentEndDate"
                @updateStartDate="editingContactOrdersFilter.currentStartDate = $event"
                @updateEndDate="editingContactOrdersFilter.currentEndDate = $event"
              />
            </ReportFilterGroup>
          </template>
          <template v-slot:filterDisplay>
            <div>
              <i class="far fa-calendar mr-2"></i>{{appliedContactOrdersFilter.previousStartDate | formatDate}} - {{appliedContactOrdersFilter.previousEndDate | formatDate}}
            </div>
            to
            <div>
              <i class="far fa-calendar mr-2"></i>{{appliedContactOrdersFilter.currentStartDate | formatDate}} - {{appliedContactOrdersFilter.currentEndDate | formatDate}}
            </div>
          </template>
        </ReportFilterPopover>
        <LoadingPanel :loading="loading" />
        <ChartBar
          title="Contact Order Value Variation"
          :series="contactVariationData.series"
          :xAxis="contactVariationData.xAxis"
          :yAxis="contactVariationData.yAxis"
          :legend="contactVariationData.legend"
          :options="contactVariationData.options"
          :popupEnabled="true"
          :exportEnabled="false"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import moment from 'moment'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'

import ChartBar from '@/components/charts/chartBar'

export default {
  name: 'ContactOrdersVariationWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValue,
    AnalyticsDashboardValueRow,
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportFilterGroup,
    ChartBar
  },
  props: {
    filter: Object,
    gridWidth: Number,
    gridHeight: Number
  },
  computed: {
  },
  data() {
    return {
      loading: true,
      summaryContactList: [],

      contactVariationData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },

      defaultContactOrdersFilter: {    
        previousStartDate: moment().local().subtract(180, 'days').startOf('day').toDate().toISOString(),
        previousEndDate: moment().local().subtract(90, 'days').endOf('day').toDate().toISOString(),
        currentStartDate: moment().local().subtract(89, 'days').startOf('day').toDate().toISOString(),
        currentEndDate: moment().local().endOf('day').toDate().toISOString()
      },
      editingContactOrdersFilter: {},
      appliedContactOrdersFilter: {},
    }
  },
  mounted() {
    this.editingContactOrdersFilter = _.clone(this.defaultContactOrdersFilter)
    this.appliedContactOrdersFilter = _.clone(this.defaultContactOrdersFilter)

    this.loading = true
    this.requestData()
  },
  watch: {
    filter: function() {
      this.reloadData()
    }
  },
  methods: {
    requestData() {
      const widgetComp = this

      var params = {
        previousStartDate: this.appliedContactOrdersFilter.previousStartDate,
        previousEndDate: this.appliedContactOrdersFilter.previousEndDate,
        currentStartDate: this.appliedContactOrdersFilter.currentStartDate,
        currentEndDate: this.appliedContactOrdersFilter.currentEndDate
      }
      if(this.filter.accountLocationIdList)
        params.accountLocationId = this.filter.accountLocationIdList
      
      this.$apiService.analyticsService.getContactOrderVariationSummary(params)
      .then(response => {
        this.summaryContactList = response.data

        // Limit to the top 10 for now
        // Reverse for the bar chart
        this.summaryContactList = this.summaryContactList.slice(0, 10)
        this.summaryContactList.reverse()

        this.contactVariationData.legend = {
          show: true,
          icon: 'none'
        }
        this.contactVariationData.xAxis = {
          type: 'value',
          position: 'top',
          splitLine: {
            lineStyle: {
              type: 'dashed'
            }
          },
          axisLabel: {
            formatter: value => widgetComp.formatCurrency(value, 0)
          }        
        }
        this.contactVariationData.yAxis = {
          type: 'category',
          axisLine: { show: false },
          axisLabel: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          data: this.summaryContactList.map(contactEntry => contactEntry.name)
        }
        this.contactVariationData.series = []
        this.contactVariationData.series.push({
          name: 'Order Value Variation',
          type: 'bar',
          label: {
            show: true,
            formatter: '{b}'
          },
          data: this.summaryContactList.map(contactEntry => {
            return {
              value: contactEntry.variationAmount,
              label: {
                position: 'right',
                formatter: contactEntry.name
              },
              itemStyle: { color: contactEntry.variationAmount >= 0 ? '#4caf50' : '#f44336'}
            }
          })
        })

        this.contactVariationData.options = {
          tooltip: {
            valueFormatter: (value) => {
              return widgetComp.formatCurrency(value);
            }
          }
        }
      })
      .catch(err => {
        console.error(err)
      })
      .finally(() => {
        this.loading = false
      })
    },
    reloadData() {
      this.requestData()
    },
    applyContactOrdersFilter() {
      this.appliedContactOrdersFilter = _.clone(this.editingContactOrdersFilter)

      this.reloadData()
    },
    resetContactOrdersFilter() {
      this.editingContactOrdersFilter = _.clone(this.defaultContactOrdersFilter)
      this.appliedContactOrdersFilter = _.clone(this.defaultContactOrdersFilter)

      this.reloadData()
    },
    
  }
}
</script>